/**
 * 数据加载 Hook —— 统一处理「加载中 / 失败 / 演示数据」三种态
 *
 * 背景：后端目前只实现了认证接口，业务接口正在并行开发。
 * 因此这里的策略是：
 *   - 接口可用 → 用真实数据；
 *   - 接口返回 404（未实现）或网络不可达 → 回退到 src/mocks 的演示数据，
 *     并在页面上明确标注「当前为演示数据」，绝不假装成功；
 *   - 权限不足 / 参数错误等真实业务错误 → 照实展示错误，不掩盖。
 * 这样页面既不白屏，也不会把假数据当成真的。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiCode } from '@ilc/shared';
import { ApiError, isUnimplementedError } from '@/api/client';

export interface ResourceResult<T> {
  data: T | null;
  loading: boolean;
  /** 真实错误（无演示数据兜底时） */
  error: ApiError | null;
  /** 当前展示的是演示数据 */
  isFallback: boolean;
  /** 为什么走了演示数据 */
  fallbackReason: string | null;
  reload: () => void;
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

/** 能否用演示数据兜底：只有「后端还没实现 / 连不上 / 外部能力不可用」才降级 */
function canFallback(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.code === ApiCode.UNAUTHORIZED) return false;
    if (err.code === ApiCode.FORBIDDEN || err.code === ApiCode.DATA_FORBIDDEN) return false;
    if (err.code === ApiCode.INVALID_PARAM) return false;
    return err.degradable || err.code === ApiCode.INTERNAL_ERROR;
  }
  return isUnimplementedError(err);
}

export interface UseResourceOptions<T> {
  /** 演示数据提供者（放 src/mocks），待后端接口就绪后移除 */
  fallback?: () => T | Promise<T>;
  /** 依赖变化时重新加载 */
  deps?: readonly unknown[];
  /** 为 false 时不自动加载（如缺少必要参数） */
  enabled?: boolean;
}

export function useResource<T>(
  loader: () => Promise<T>,
  options: UseResourceOptions<T> = {},
): ResourceResult<T> {
  const { fallback, deps = [], enabled = true } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<ApiError | null>(null);
  const [isFallback, setIsFallback] = useState(false);
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const result = await loaderRef.current();
        if (cancelled) return;
        setData(result);
        setIsFallback(false);
        setFallbackReason(null);
      } catch (err) {
        if (cancelled) return;
        const apiError =
          err instanceof ApiError
            ? err
            : new ApiError({
                message: err instanceof Error ? err.message : '加载失败',
                code: ApiCode.INTERNAL_ERROR,
              });

        if (fallbackRef.current && canFallback(apiError)) {
          try {
            const mocked = await fallbackRef.current();
            if (cancelled) return;
            setData(mocked);
            setIsFallback(true);
            setFallbackReason(apiError.message);
            setError(null);
          } catch {
            if (cancelled) return;
            setError(apiError);
            setIsFallback(false);
          }
        } else {
          setError(apiError);
          setIsFallback(false);
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, version, ...deps]);

  return { data, loading, error, isFallback, fallbackReason, reload, setData };
}

export default useResource;
